import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useCurrentUser } from '../account/useAuth';
import {
  useAddStudent,
  useApproveSubmission,
  useBroadcastSubmission,
  useClassroom,
  useClassroomAssignments,
  useClassroomControl,
  useClearBroadcast,
  useLiveView,
  usePatchClassroom,
  usePauseClassroom,
  useRemoveStudent,
  useResetStudentPin,
  useResumeClassroom,
  useReviewQueue,
  useClassroomPublishedApps,
  useStudents,
  useUnpinAssignment,
  useUpdateStudent,
} from './useTeacher';
import { classroomBlobUrl, gradebookCsvUrl } from './teacherApi';
import { RevealableInput } from '../components/RevealableInput/RevealableInput';
import './ClassroomDetail.css';

type Tab = 'live' | 'roster' | 'assignments' | 'review' | 'published';

/**
 * The teacher's per-classroom workspace — `/teacher/classrooms/:id`.
 *
 * One pane, four tabs:
 *   * Live view — per-student dashboard, polls every 10s.
 *   * Roster — add / remove kids, reset PINs, see the class code.
 *   * Assignments — pinned units + a link to the lesson library.
 *   * Review queue — pending submissions, approve/reject.
 *
 * The header shows the class code prominently so a teacher reading
 * it aloud doesn't have to navigate away.
 */
export function ClassroomDetail(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { data: user, isPending: userPending } = useCurrentUser();
  const classroom = useClassroom(id);
  const [tab, setTab] = useState<Tab>('live');

  if (userPending) return <div className="bl-cls" data-testid="cls-loading" />;
  if (!user || (user.role !== 'teacher' && user.role !== 'admin')) {
    return (
      <div className="bl-cls">
        <p className="bl-cls__notice">
          This page is for teachers. <Link to="/studio">Open the studio →</Link>
        </p>
      </div>
    );
  }

  if (classroom.isPending) {
    return <div className="bl-cls bl-cls__notice">Loading classroom…</div>;
  }
  if (classroom.isError || !classroom.data) {
    return (
      <div className="bl-cls bl-cls__notice">
        Classroom not found. <Link to="/teacher">Back to your classrooms →</Link>
      </div>
    );
  }

  return (
    <div className="bl-cls">
      <header className="bl-cls__header">
        <Link to="/teacher" className="bl-cls__back">
          ← Your classrooms
        </Link>
        <h1 className="bl-cls__title">{classroom.data.name}</h1>
        <div className="bl-cls__header-row">
          <div className="bl-cls__code-row">
            <span className="bl-cls__code-label">Class code</span>
            <code className="bl-cls__code">{classroom.data.class_code}</code>
          </div>
          <ControlPanel classroomId={id} />
          {/* Native browser download — no auth header gymnastics; the
              cookie rides the request because we're same-site. */}
          <a
            href={gradebookCsvUrl(id)}
            className="bl-cls__btn bl-cls__btn--secondary"
            data-testid="gradebook-download"
            download
            title="Download every rubric mark in this classroom as CSV."
            aria-label="Download gradebook CSV"
          >
            <span aria-hidden="true">↓ </span>Gradebook CSV
          </a>
        </div>
      </header>

      <nav className="bl-cls__tabs">
        <TabBtn active={tab === 'live'} onClick={() => setTab('live')} label="Live class" />
        <TabBtn active={tab === 'roster'} onClick={() => setTab('roster')} label="Roster" />
        <TabBtn
          active={tab === 'assignments'}
          onClick={() => setTab('assignments')}
          label="Assignments"
        />
        <TabBtn active={tab === 'review'} onClick={() => setTab('review')} label="Review" />
        <TabBtn
          active={tab === 'published'}
          onClick={() => setTab('published')}
          label="Published"
        />
      </nav>

      <div className="bl-cls__panel" role="tabpanel">
        {tab === 'live' ? <LiveView classroomId={id} /> : null}
        {tab === 'roster' ? <Roster classroomId={id} /> : null}
        {tab === 'assignments' ? <Assignments classroomId={id} /> : null}
        {tab === 'review' ? <ReviewQueue classroomId={id} /> : null}
        {tab === 'published' ? <PublishedFeed classroomId={id} /> : null}
      </div>
    </div>
  );
}

function TabBtn(props: { active: boolean; onClick: () => void; label: string }): JSX.Element {
  return (
    <button
      type="button"
      className={props.active ? 'bl-cls__tab bl-cls__tab--active' : 'bl-cls__tab'}
      onClick={props.onClick}
      role="tab"
      aria-selected={props.active}
    >
      {props.label}
    </button>
  );
}

// --- Live view -----------------------------------------------------------

function LiveView({ classroomId }: { classroomId: string }): JSX.Element {
  const { data, isPending } = useLiveView(classroomId);
  const control = useClassroomControl(classroomId);
  const broadcast = useBroadcastSubmission(classroomId);
  const clear = useClearBroadcast(classroomId);
  if (isPending) {
    return <p className="bl-cls__notice">Loading live view…</p>;
  }
  if (!data || data.length === 0) {
    return (
      <div className="bl-cls__empty">
        <p>No students yet.</p>
        <p className="bl-cls__empty-hint">
          Add students from the Roster tab — they sign in with the class code.
        </p>
      </div>
    );
  }
  const liveBroadcastId = control.data?.broadcast?.submission_id ?? null;
  return (
    <ul className="bl-cls__tile-grid" data-testid="live-tiles">
      {data.map((tile) => (
        <li
          key={tile.student_id}
          className={tile.stuck ? 'bl-cls__tile bl-cls__tile--stuck' : 'bl-cls__tile'}
        >
          <div className="bl-cls__tile-head">
            {tile.latest_sample_blob_id &&
            (tile.latest_sample_kind === 'image' || tile.latest_sample_kind === 'pose') ? (
              <img
                className="bl-cls__tile-thumb"
                src={classroomBlobUrl(classroomId, tile.latest_sample_blob_id)}
                alt={`${tile.display_name ?? 'student'} sample`}
                loading="lazy"
              />
            ) : tile.latest_sample_blob_id && tile.latest_sample_kind === 'audio' ? (
              // A teacher triaging 30 audio kids needs a play button,
              // not a 🔊 emoji. Native <audio> with preload="none"
              // means we don't fetch any bytes until the teacher
              // clicks — 30 controls cost nothing on page load.
              // eslint-disable-next-line jsx-a11y/media-has-caption -- raw kid sample, no captions to attach
              <audio
                className="bl-cls__tile-audio"
                src={classroomBlobUrl(classroomId, tile.latest_sample_blob_id)}
                controls
                preload="none"
                aria-label={`Play ${tile.display_name ?? 'student'} sample`}
              />
            ) : (
              <div className="bl-cls__tile-thumb bl-cls__tile-thumb--empty" aria-hidden="true" />
            )}
            <div className="bl-cls__tile-info">
              <div className="bl-cls__tile-name">
                {tile.display_name ?? '(no name)'}
                {tile.stuck ? (
                  <span className="bl-cls__tile-stuck" title="No activity recently">
                    stuck?
                  </span>
                ) : null}
              </div>
              <div className="bl-cls__tile-meta">
                {tile.current_project_name ? (
                  <span>📁 {tile.current_project_name}</span>
                ) : (
                  <span className="bl-cls__tile-quiet">no project yet</span>
                )}
              </div>
              <div className="bl-cls__tile-meta">
                <span>📷 {tile.sample_count} samples</span>
                {tile.submission_status ? (
                  <span className="bl-cls__tile-badge">{humanStatus(tile.submission_status)}</span>
                ) : null}
              </div>
              <div className="bl-cls__tile-meta bl-cls__tile-quiet">
                {tile.last_seen_at
                  ? `Last seen ${humanizeRelative(tile.last_seen_at)}`
                  : 'Not seen yet'}
              </div>
              {tile.latest_submission_id ? (
                liveBroadcastId === tile.latest_submission_id ? (
                  <button
                    type="button"
                    className="bl-cls__tile-broadcast bl-cls__tile-broadcast--on"
                    onClick={() => clear.mutate()}
                    disabled={clear.isPending}
                    title="Stop showing this kid's project to the class."
                  >
                    📡 On the board · stop
                  </button>
                ) : (
                  <button
                    type="button"
                    className="bl-cls__tile-broadcast"
                    onClick={() => broadcast.mutate(tile.latest_submission_id!)}
                    disabled={broadcast.isPending}
                    title="Spotlight this kid's project for everyone in the class."
                  >
                    📡 Broadcast
                  </button>
                )
              ) : null}
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function humanStatus(status: string): string {
  // Kid-friendly labels for the statuses surfaced on a tile. Mirrors
  // the public copy elsewhere in the studio.
  switch (status) {
    case 'awaiting_teacher_review':
      return 'waiting on you';
    case 'awaiting_parent_consent':
      return 'waiting on grown-up';
    case 'processing':
      return 'checking…';
    case 'approved':
      return 'approved';
    case 'rejected':
      return 'returned';
    case 'withdrawn':
      return 'pulled';
    default:
      return status;
  }
}

function humanizeRelative(iso: string): string {
  const seconds = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return `${Math.round(seconds)}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86_400)}d ago`;
}

// --- Roster --------------------------------------------------------------

function Roster({ classroomId }: { classroomId: string }): JSX.Element {
  const { data: students, isPending } = useStudents(classroomId);
  const addMutation = useAddStudent(classroomId);
  const removeMutation = useRemoveStudent(classroomId);
  const resetMutation = useResetStudentPin(classroomId);
  const updateMutation = useUpdateStudent(classroomId);
  const [name, setName] = useState('');
  const [pin, setPin] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [editingEmail, setEditingEmail] = useState<{
    studentId: string;
    email: string;
  } | null>(null);
  const [resetPin, setResetPin] = useState<{
    studentId: string;
    pin: string;
  } | null>(null);

  const handleAdd = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!name.trim() || !pin.trim() || addMutation.isPending) return;
    const args: {
      display_name: string;
      pin: string;
      parent_contact_email?: string | null;
    } = { display_name: name.trim(), pin: pin.trim() };
    if (parentEmail.trim()) {
      args.parent_contact_email = parentEmail.trim();
    }
    addMutation.mutate(args, {
      onSuccess: () => {
        setName('');
        setPin('');
        setParentEmail('');
      },
    });
  };

  const handleResetPin = (studentId: string): void => {
    resetMutation.mutate(
      { studentId },
      {
        onSuccess: (response) => {
          setResetPin({ studentId, pin: response.pin });
        },
      },
    );
  };

  return (
    <div>
      <form onSubmit={handleAdd} className="bl-cls__add-row">
        <input
          type="text"
          placeholder="Kid's name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={80}
          aria-label="Student name"
          className="bl-cls__input"
        />
        <RevealableInput
          defaultType="tel"
          placeholder="4-digit PIN"
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          maxLength={8}
          inputMode="numeric"
          aria-label="Student PIN"
          className="bl-cls__input bl-cls__input--pin"
        />
        <input
          type="email"
          placeholder="Parent email (optional)"
          value={parentEmail}
          onChange={(e) => setParentEmail(e.target.value)}
          maxLength={320}
          aria-label="Parent contact email"
          className="bl-cls__input"
        />
        <button
          type="submit"
          className="bl-cls__btn"
          disabled={!name.trim() || !pin.trim() || addMutation.isPending}
        >
          Add
        </button>
      </form>
      <p className="bl-cls__notice" style={{ fontSize: 12, marginTop: -8 }}>
        Parent email is optional, only needed to promote work to the public gallery later.
      </p>
      {addMutation.error instanceof Error ? (
        <p className="bl-cls__error">{addMutation.error.message}</p>
      ) : null}

      {isPending ? (
        <p className="bl-cls__notice">Loading roster…</p>
      ) : !students || students.length === 0 ? (
        <p className="bl-cls__notice">No students yet — add one above.</p>
      ) : (
        <ul className="bl-cls__roster-list" data-testid="roster-list">
          {students.map((s) => (
            <li key={s.id} className="bl-cls__roster-row">
              <div>
                <span className="bl-cls__roster-name">{s.display_name}</span>
                <div className="bl-cls__roster-sub">
                  {s.has_parent_contact ? (
                    <span title={s.parent_contact_email ?? undefined}>
                      📧 {s.parent_contact_email}
                    </span>
                  ) : (
                    <span className="bl-cls__roster-quiet">
                      no parent email — public-promote unavailable
                    </span>
                  )}
                </div>
              </div>
              {resetPin && resetPin.studentId === s.id ? (
                <span className="bl-cls__pin-display">
                  New PIN: <strong>{resetPin.pin}</strong>{' '}
                  <button
                    type="button"
                    className="bl-cls__link-btn"
                    onClick={() => setResetPin(null)}
                  >
                    Got it
                  </button>
                </span>
              ) : editingEmail && editingEmail.studentId === s.id ? (
                <span className="bl-cls__roster-actions">
                  <input
                    type="email"
                    value={editingEmail.email}
                    onChange={(e) =>
                      setEditingEmail({
                        studentId: s.id,
                        email: e.target.value,
                      })
                    }
                    placeholder="parent@example.com"
                    className="bl-cls__input"
                    style={{ flex: '1 1 200px' }}
                    aria-label="Parent contact email"
                  />
                  <button
                    type="button"
                    className="bl-cls__link-btn"
                    onClick={() => {
                      updateMutation.mutate(
                        {
                          studentId: s.id,
                          parent_contact_email: editingEmail.email || null,
                        },
                        { onSuccess: () => setEditingEmail(null) },
                      );
                    }}
                    disabled={updateMutation.isPending}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="bl-cls__link-btn"
                    onClick={() => setEditingEmail(null)}
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <span className="bl-cls__roster-actions">
                  <button
                    type="button"
                    className="bl-cls__link-btn"
                    onClick={() =>
                      setEditingEmail({
                        studentId: s.id,
                        email: s.parent_contact_email ?? '',
                      })
                    }
                  >
                    {s.has_parent_contact ? 'Edit email' : 'Add email'}
                  </button>
                  <button
                    type="button"
                    className="bl-cls__link-btn"
                    onClick={() => handleResetPin(s.id)}
                    disabled={resetMutation.isPending}
                  >
                    Reset PIN
                  </button>
                  <button
                    type="button"
                    className="bl-cls__link-btn bl-cls__link-btn--danger"
                    onClick={() => removeMutation.mutate(s.id)}
                    disabled={removeMutation.isPending}
                  >
                    Remove
                  </button>
                </span>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Assignments ---------------------------------------------------------

function Assignments({ classroomId }: { classroomId: string }): JSX.Element {
  const { data, isPending } = useClassroomAssignments(classroomId);
  const unpinMutation = useUnpinAssignment(classroomId);

  return (
    <div>
      <div className="bl-cls__assignments-header">
        <p className="bl-cls__notice" style={{ margin: 0 }}>
          Pinned assignments show up on each kid&apos;s home screen.
        </p>
        <Link to="/teacher/lessons" className="bl-cls__link-btn">
          + Add from lesson library
        </Link>
      </div>

      {isPending ? (
        <p className="bl-cls__notice">Loading…</p>
      ) : !data || data.length === 0 ? (
        <p className="bl-cls__notice">
          No assignments pinned yet. Pick one from the lesson library.
        </p>
      ) : (
        <ul className="bl-cls__assignment-list">
          {data.map((a) => (
            <li key={a.id} className="bl-cls__assignment-row">
              <div>
                <div className="bl-cls__assignment-name">{a.assignment_name}</div>
                <div className="bl-cls__assignment-meta">
                  {a.assignment_kind} · {a.status}
                  {a.assignment_time_estimate_minutes
                    ? ` · ~${a.assignment_time_estimate_minutes} min`
                    : ''}
                </div>
              </div>
              <button
                type="button"
                className="bl-cls__link-btn bl-cls__link-btn--danger"
                onClick={() => unpinMutation.mutate(a.id)}
                disabled={unpinMutation.isPending}
              >
                Unpin
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Review queue --------------------------------------------------------

function ReviewQueue({ classroomId }: { classroomId: string }): JSX.Element {
  const { data, isPending } = useReviewQueue(classroomId);
  const approve = useApproveSubmission(classroomId);

  return (
    <div>
      {isPending ? (
        <p className="bl-cls__notice">Loading review queue…</p>
      ) : !data || data.length === 0 ? (
        <div className="bl-cls__empty">
          <p>Nothing waiting on review right now. ✨</p>
        </div>
      ) : (
        <ul className="bl-cls__review-list" data-testid="review-list">
          {data.map((item) => (
            <li key={item.submission_id} className="bl-cls__review-row">
              <div>
                <div className="bl-cls__review-name">{item.project_name}</div>
                <div className="bl-cls__review-meta">
                  by {item.submitter_display_name ?? 'a kid'} · {item.project_kind} ·{' '}
                  {humanStatus(item.status)}
                </div>
              </div>
              <div className="bl-cls__review-actions">
                <Link
                  to={`/teacher/classrooms/${classroomId}/submissions/${item.submission_id}/grade`}
                  className="bl-cls__btn bl-cls__btn--secondary"
                >
                  Grade
                </Link>
                <button
                  type="button"
                  className="bl-cls__btn"
                  onClick={() => approve.mutate(item.submission_id)}
                  disabled={approve.isPending || item.status !== 'awaiting_teacher_review'}
                >
                  Approve
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// --- Published-apps feed (Phase 4.5c) ------------------------------------

function PublishedFeed({ classroomId }: { classroomId: string }): JSX.Element {
  const feed = useClassroomPublishedApps(classroomId);
  const classroom = useClassroom(classroomId);
  const patch = usePatchClassroom(classroomId);
  const allow = !!classroom.data?.allow_publish;
  const toggle = (): void => {
    patch.mutate({ allow_publish: !allow });
  };
  return (
    <div className="bl-cls__published-wrap">
      <div className="bl-cls__published-toggle" role="group" aria-label="Publishing settings">
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control -- label wraps both checkbox + nested-span title; lint rule misses the nested text node */}
        <label className="bl-cls__published-toggle-row">
          <input type="checkbox" checked={allow} onChange={toggle} disabled={patch.isPending} />
          <span>
            <strong>Allow students to publish to bryta.app</strong>
            <span className="bl-cls__published-toggle-hint">
              Off by default. When on, kids on this roster can ship their trained models to a real
              bryta.app URL — visible to anyone with the link. Turn it back off to block new
              publishes (existing published apps stay live until you remove them individually).
            </span>
          </span>
        </label>
      </div>
      <PublishedFeedList feed={feed} />
    </div>
  );
}

interface FeedQueryShape {
  isLoading: boolean;
  isError: boolean;
  data: ReturnType<typeof useClassroomPublishedApps>['data'];
}

function PublishedFeedList({ feed }: { feed: FeedQueryShape }): JSX.Element {
  if (feed.isLoading) {
    return <p className="bl-cls__notice">Loading apps your class has shipped…</p>;
  }
  if (feed.isError) {
    return (
      <p className="bl-cls__notice bl-cls__notice--error">
        Could not load the feed. Refresh the page or try again later.
      </p>
    );
  }
  const apps = feed.data ?? [];
  if (apps.length === 0) {
    return (
      <p className="bl-cls__notice">
        Nobody on the roster has published an app to bryta.app yet. When a student clicks{' '}
        <strong>Publish to bryta.app</strong> in the studio, their app will appear here.
      </p>
    );
  }
  return (
    <ul className="bl-cls__published" aria-label="Published apps">
      {apps.map((app) => {
        const updated = new Date(app.updatedAt);
        const updatedLabel = isNaN(updated.getTime())
          ? app.updatedAt
          : updated.toLocaleString(undefined, {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            });
        return (
          <li key={app.id} className="bl-cls__published-row">
            <div className="bl-cls__published-head">
              <p className="bl-cls__published-title">{app.title}</p>
              <p className="bl-cls__published-meta">
                <span>by {app.studentName}</span>
                <span>v{app.version}</span>
                {app.viewCount > 0 && (
                  <span aria-label={`${app.viewCount} visits`}>
                    <span aria-hidden="true">👀 </span>
                    {app.viewCount === 1 ? '1 visit' : `${app.viewCount} visits`}
                  </span>
                )}
                <span className="bl-cls__published-updated">Updated {updatedLabel}</span>
              </p>
            </div>
            <a
              className="bl-cls__published-link"
              href={app.url}
              target="_blank"
              rel="noopener noreferrer"
            >
              Open live ↗
            </a>
          </li>
        );
      })}
    </ul>
  );
}

// --- Control panel (Phase 4.5b.14) ---------------------------------------

function ControlPanel({ classroomId }: { classroomId: string }): JSX.Element {
  const control = useClassroomControl(classroomId);
  const pause = usePauseClassroom(classroomId);
  const resume = useResumeClassroom(classroomId);
  const isPaused = Boolean(control.data?.paused_at);

  return (
    <div
      className={isPaused ? 'bl-cls__control bl-cls__control--paused' : 'bl-cls__control'}
      data-testid="control-panel"
    >
      <span className="bl-cls__control-status">
        {isPaused ? '⏸ Class paused' : '● Class active'}
      </span>
      {isPaused ? (
        <button
          type="button"
          className="bl-cls__btn"
          onClick={() => resume.mutate()}
          disabled={resume.isPending}
        >
          {resume.isPending ? 'Resuming…' : 'Resume class'}
        </button>
      ) : (
        <button
          type="button"
          className="bl-cls__btn bl-cls__btn--secondary"
          onClick={() => pause.mutate()}
          disabled={pause.isPending}
          title="Freeze the studio for everyone — discussion mode."
        >
          {pause.isPending ? 'Pausing…' : 'Pause everyone'}
        </button>
      )}
    </div>
  );
}
